import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $insertNodes,
  $createTextNode,
} from "lexical";
import { $createMentionNode } from "./MentionNode";

const DUMMY_USERS = [
  { id: "1", name: "John Doe" },
  { id: "2", name: "Jane Smith" },
  { id: "3", name: "Hardeep Kaur" },
  { id: "4", name: "Mia Alvarez" },
  { id: "5", name: "Lena Olsson" },
];

export const MentionPlugin = () => {
  const [editor] = useLexicalComposerContext();
  
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(DUMMY_USERS);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    // register an update listener so we always get latest selection/text
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        // hide if selection isn't a range selection
        if (!$isRangeSelection(selection)) {
          setOpen(false);
          return;
        }

        const anchorNode = selection.anchor.getNode();
        if (!$isTextNode(anchorNode)) {
          setOpen(false);
          return;
        }

        const text = anchorNode.getTextContent();
        const beforeCursor = text.slice(0, selection.anchor.offset);
        const atIndex = beforeCursor.lastIndexOf("@");

        if (atIndex === -1) {
          setOpen(false);
          return;
        }

        const q = beforeCursor.slice(atIndex + 1);
        setQuery(q);

        // simple filter - plug your API here
        const filtered = DUMMY_USERS.filter((u) =>
          u.name.toLowerCase().includes(q.toLowerCase())
        );
        setResults(filtered);

        // compute caret DOM range and position
        let domRange = null;
        try {
          domRange = selection.getDOMRange && selection.getDOMRange();
        } catch (e) {
          domRange = null;
        }

        if (!domRange) {
          const winSel = window.getSelection?.();
          if (winSel && winSel.rangeCount > 0) {
            domRange = winSel.getRangeAt(0);
          }
        }

        if (domRange) {
          const rect = domRange.getBoundingClientRect();
          // place dropdown below caret (use page scroll offsets)
          setCoords({
            top: rect.bottom + window.scrollY + 6,
            left: rect.left + window.scrollX,
          });
          setOpen(true);
        } else {
          // fallback place near top-left of editor root
          const rootEl = editor.getRootElement?.();
          if (rootEl) {
            const r = rootEl.getBoundingClientRect();
            setCoords({
              top: r.top + window.scrollY + 30,
              left: r.left + window.scrollX + 10,
            });
            setOpen(true);
          } else {
            setOpen(false);
          }
        }
      });
    });

    return () => unregister();
  }, [editor]);

  const insertMention = (user) => {
    // insert mention node replacing @query
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const anchorNode = selection.anchor.getNode();
      if (!$isTextNode(anchorNode)) return;

      const textContent = anchorNode.getTextContent();
      const beforeCursor = textContent.slice(0, selection.anchor.offset);
      const atIndex = beforeCursor.lastIndexOf("@");
      if (atIndex === -1) return;

      // remove the @query text
      anchorNode.spliceText(atIndex, selection.anchor.offset, "");

      // create mention node and insert it
      const mentionNode = $createMentionNode(user.id, user.name);
      $insertNodes([mentionNode]);

      // ensure a space after the mention so caret moves out
      $insertNodes([$createTextNode(" ")]);
    });

    setOpen(false);
    setQuery("");
  };

  // If closed, render nothing
  if (!open) return null;

  // Render the dropdown into document.body so it won't be clipped by overflow
  return createPortal(
    <div
      ref={containerRef}
      role="listbox"
      aria-label="Mention Suggestions"
      style={{
        position: "absolute",
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        background: "#fff",
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
        borderRadius: 8,
        width: 220,
        zIndex: 9999,
        padding: 6,
      }}
    >
      {results.length === 0 ? (
        <div style={{ padding: "8px 10px", color: "#6b7280" }}>No users</div>
      ) : (
        results.map((u) => (
          <div
            key={u.id}
            role="option"
            onMouseDown={(e) => {
              e.preventDefault();
              insertMention(u);
            }}
            style={{
              padding: "8px 10px",
              cursor: "pointer",
              borderRadius: 6,
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.03)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            {u.name}
          </div>
        ))
      )}
    </div>,
    document.body
  );
};
